import type { SanityHomePagePainpoint } from '#/lib/sanity/queries/sections';
import { useMediaQuery } from '#/lib/useMatchMedia';
import { cn } from '#/lib/utils';
import { formatHeadline } from '#/components/Headline';

import { Overline } from '../Overline';
import { RichText } from '../RichText';
import { DesktopAnimation } from './DesktopAnimation';
import { DesktopGradient } from './DesktopGradient';
import { MobileAnimation } from './MobileAnimation';
import { MobileGradient } from './MobileGradient';
import { TabletAnimation } from './TabletAnimation';

export function HomePagePainpoint(props: SanityHomePagePainpoint) {
  const [desktopMatches, hasRunDesktop] = useMediaQuery({ screen: 1200 });
  const [matchesTablet, hasRunTablet] = useMediaQuery({
    query: '(min-width: 800px) and (max-width: 1199px)',
  });

  const shouldShowDesktopMedia = desktopMatches && hasRunDesktop;
  const shouldShowTabletMedia = matchesTablet && hasRunTablet;
  const showMobileMedia = !shouldShowDesktopMedia && !shouldShowTabletMedia;
  return (
    <div className="bg-charcoal text-cloud m:pt-90 relative">
      <div
        className={cn(
          'flex flex-col items-center max-w-400 m:max-w-750 mx-auto',
          'px-15 m:px-0 text-center text-balance relative z-10',
        )}
      >
        {props.eyebrow && (
          <Overline className="mb-0 text-electric" as="span">
            {props.eyebrow}
          </Overline>
        )}
        {props.headline && (
          <h2 className="my-22 m:my-22 text-28 m:text-35 l:text-42 font-[440] leading-105 -tracking-1">
            {formatHeadline(props.headline, 'text-electric')}
          </h2>
        )}

        {props.bodyCopy && props.bodyCopy.length > 0 && (
          <RichText
            value={props.bodyCopy}
            className="text-body l:text-18 m:whitespace-pre-wrap !text-current max-w-[585px]"
          />
        )}
      </div>
      <div className="relative z-10">
        <div className="fade-in mt-40 max-w-[1700px] mx-auto px-20  pb-120 l:px-30 xl:px-80 l:pb-150">
          {shouldShowDesktopMedia && <DesktopAnimation />}
          {shouldShowTabletMedia && <TabletAnimation />}
          {showMobileMedia && <MobileAnimation />}
        </div>
      </div>
      {/* The gradients render themselves into a canvas (blur included) so the
          browser composites a static bitmap. Live CSS/SVG gradients + blur
          get re-rasterized while the GSAP animation runs, causing visible
          seams/banding (most visible in Firefox). */}
      {shouldShowDesktopMedia ? (
        <div className="overflow-clip max-w-[100vw] w-full h-[85%] object-center absolute bottom-0 hidden m:block z-1">
          <DesktopGradient className="absolute -bottom-100 left-1/2 -translate-x-1/2 h-full min-w-full" />
        </div>
      ) : (
        <div className="overflow-clip max-w-[100vw] w-full h-[90%] object-center absolute bottom-0 px-30 z-1">
          <MobileGradient className="absolute -bottom-100 left-1/2 -translate-x-1/2 h-full min-w-full" />
        </div>
      )}
    </div>
  );
}
